/**
 * The marks an analyst leaves on a node or an edge (PRD-0075 R-C3, R-C4, ADR-0093), read from the
 * properties the graph reads answer — the names are the server's (`knowledge_graph/analyst_marks.py`):
 *
 * - *Pridať fakt* pins a key: `_asserted_keys`, provenance per key in `_assertions` (JSON);
 * - *Už neplatí od* on an edge: the flat `_invalid_*` stamps; on a property: `_invalid_keys` and
 *   `_invalidations` (JSON per key);
 * - *Chybné*: `_tombstoned` with the `_retired_*` stamps (an excerpt's tombstone has none).
 *
 * What no longer holds and what is wrong are hidden by default; the "show no longer valid" toggle
 * brings them back, greyed.
 */
import type { GraphView } from '@/types/graph-view'

type Properties = Record<string, unknown>

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null

function jsonObject(value: unknown): Record<string, unknown> {
  if (typeof value !== 'string') return {}
  try {
    const parsed: unknown = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

/** Where the analyst has it from. */
export interface FactSourceView {
  text: string | null
  documentId: string | null
  documentName: string | null
}

function sourceOf(value: unknown): FactSourceView | null {
  if (!value || typeof value !== 'object') return null
  const source = value as Record<string, unknown>
  const view = {
    text: text(source.text),
    documentId: text(source.document_id),
    documentName: text(source.document_name),
  }
  return view.text || view.documentId ? view : null
}

/** One pinned property: its value now and who asserted it, when, from where, valid when. */
export interface AssertionView {
  key: string
  value: unknown
  byName: string | null
  at: string | null
  source: FactSourceView | null
  validFrom: string | null
  validTo: string | null
}

/** The properties an analyst asserted, in the order they were pinned. */
export function assertionsOf(properties: Properties): AssertionView[] {
  const records = jsonObject(properties._assertions)
  return stringList(properties._asserted_keys).map(key => {
    const record = (records[key] ?? {}) as Record<string, unknown>
    return {
      key,
      value: properties[key],
      byName: text(record.by_name),
      at: text(record.at),
      source: sourceOf(record.source),
      validFrom: text(record.valid_from),
      validTo: text(record.valid_to),
    }
  })
}

/** A fact no longer holding: since when, why, who said so and when. */
export interface InvalidationView {
  from: string
  reason: string | null
  byName: string | null
  at: string | null
  source: FactSourceView | null
}

/** An edge's *Už neplatí od*; null while it holds. */
export function relationshipInvalidation(properties: Properties): InvalidationView | null {
  const from = text(properties._invalid_from)
  if (!from) return null
  return {
    from,
    reason: text(properties._invalid_reason),
    byName: text(properties._invalid_by_name),
    at: text(properties._invalid_at),
    source: sourceOf(jsonObject(properties._invalid_source)),
  }
}

/** The properties of a node marked no longer valid, by key. */
export function propertyInvalidations(properties: Properties): Map<string, InvalidationView> {
  const records = jsonObject(properties._invalidations)
  const marked = new Map<string, InvalidationView>()
  for (const key of stringList(properties._invalid_keys)) {
    const record = (records[key] ?? {}) as Record<string, unknown>
    marked.set(key, {
      from: text(record.from) ?? '',
      reason: text(record.reason),
      byName: text(record.by_name),
      at: text(record.at),
      source: sourceOf(record.source),
    })
  }
  return marked
}

/** *Chybné*: who marked it wrong, when, why. */
export interface RetirementView {
  reason: string | null
  byName: string | null
  at: string | null
}

/** An analyst's retirement of a node or an edge; null for a live one or an excerpt's tombstone. */
export function retirementOf(properties: Properties): RetirementView | null {
  if (properties._tombstoned !== true || !text(properties._retired_at)) return null
  return {
    reason: text(properties._retired_reason),
    byName: text(properties._retired_by_name),
    at: text(properties._retired_at),
  }
}

/** A property marked no longer valid, with the value it holds. */
export interface InvalidPropertyView {
  key: string
  value: unknown
  invalidation: InvalidationView
}

/** What the entity detail shows of an analyst's marks on one entity. */
export interface EntityFactsModel {
  assertions: AssertionView[]
  invalid: InvalidPropertyView[]
  retirement: RetirementView | null
}

export function entityFactsModel(properties: Properties): EntityFactsModel {
  return {
    assertions: assertionsOf(properties),
    invalid: [...propertyInvalidations(properties)].map(([key, invalidation]) => ({
      key,
      value: properties[key],
      invalidation,
    })),
    retirement: retirementOf(properties),
  }
}

/** `properties` without the ones marked no longer valid: what a properties list shows by default. */
export function withoutInvalidProperties(properties: Properties): Properties {
  const invalid = propertyInvalidations(properties)
  if (invalid.size === 0) return properties
  return Object.fromEntries(Object.entries(properties).filter(([key]) => !invalid.has(key)))
}

/** Hidden unless the toggle shows it: an edge no longer valid, a node or edge marked wrong. */
export function isHiddenFact(properties: Properties | undefined): boolean {
  if (!properties) return false
  return retirementOf(properties) !== null || relationshipInvalidation(properties) !== null
}

interface Item {
  id: string
  properties?: Properties
}

interface Link extends Item {
  source: string
  target: string
}

/**
 * What a view shows of `nodes` and `edges`: without the hidden facts, or (`show`) with them and
 * their ids in `faded`, to be drawn greyed. An edge of a hidden node goes with it.
 */
export function applyFactVisibility<N extends Item, E extends Link>(
  nodes: readonly N[],
  edges: readonly E[],
  show: boolean
): { nodes: N[]; edges: E[]; faded: Set<string> } {
  const faded = new Set<string>()
  for (const item of [...nodes, ...edges]) if (isHiddenFact(item.properties)) faded.add(item.id)
  if (faded.size === 0 || show) return { nodes: [...nodes], edges: [...edges], faded }
  const shown = nodes.filter(node => !faded.has(node.id))
  const ids = new Set(shown.map(node => node.id))
  return {
    nodes: shown,
    edges: edges.filter(e => !faded.has(e.id) && ids.has(e.source) && ids.has(e.target)),
    faded: new Set(),
  }
}

/** Ids a view greys for any reason (a time filter, a mark); undefined when none. */
export function unionIds(
  ...sets: ReadonlyArray<ReadonlySet<string> | null | undefined>
): Set<string> | undefined {
  const ids = new Set<string>()
  for (const set of sets) for (const id of set ?? []) ids.add(id)
  return ids.size > 0 ? ids : undefined
}

/** `view` with the node or edge `id` holding `properties`; null when the view holds no such item. */
export function withProperties(
  view: GraphView,
  id: string,
  properties: Properties
): GraphView | null {
  if (view.nodes.some(node => node.id === id)) {
    return {
      ...view,
      nodes: view.nodes.map(node => (node.id === id ? { ...node, properties } : node)),
    }
  }
  if (view.edges.some(edge => edge.id === id)) {
    return {
      ...view,
      edges: view.edges.map(edge => (edge.id === id ? { ...edge, properties } : edge)),
    }
  }
  return null
}
